const medications = (count: number) => `medication${count === 1 ? '' : 's'}`;
const fillRecords = (count: number) => `fill record${count === 1 ? '' : 's'}`;

export const medRec = {
  tab: 'MedRec',
  hint: 'Move each medication the patient is still taking to Reconciled, then Lock & Certify.',
  showing: (current: number, total: number) => `Showing ${current} current medications of ${total}`,
  searchPlaceholder: 'Search medications',
  needsReconciliation: {
    title: (count: number) => `Needs reconciliation · ${count}`,
    titleFiltered: (shown: number, total: number) => `Needs reconciliation · ${shown} of ${total}`,
    allHidden: (total: number) => `${total} hidden by search or filters.`,
    subtitle: 'Not confirmed yet. Move the ones the patient is taking to the right.',
    addSelected: (count: number) => `Add ${count} selected`,
    addTooltip: 'Mark as reconciled',
    addAriaLabel: (name: string) => `Mark ${name} as reconciled`,
    dropLabel: 'Drag here to move back to needs reconciliation',
    allReconciled: "Everything is reconciled. Lock & Certify when you're ready.",
    previouslyReconciled: {
      title: (count: number) => `Previously reconciled · ${count}`,
      caption: 'Confirmed before. They will need review again unless you add them back.',
      addAllBack: 'Add all back'
    },
    notReviewed: {
      title: (count: number) => `Not reviewed yet · ${count}`,
      caption: 'Add the ones the patient is taking',
      addAll: 'Add all'
    }
  },
  reconciled: {
    title: (count: number) => `Reconciled · ${count}`,
    titleFiltered: (shown: number, total: number) => `Reconciled · ${shown} of ${total}`,
    subtitle: 'Confirmed with the patient. Saved when you Lock & Certify.',
    clearList: 'Clear list',
    removeTooltip: 'Move back to needs reconciliation',
    removeAriaLabel: (name: string) => `Move ${name} back to needs reconciliation`,
    dropLabel: 'Drag here, or use + to mark as reconciled',
    empty: 'Nothing here yet. Add the medications the patient is taking.',
    allHidden: (total: number) => `${total} hidden by search or filters. Still included when you Lock & Certify.`
  },
  duplicate: {
    sameName: 'Another entry for this drug is already in Reconciled. Add this one only if the patient follows these directions too.',
    alsoListedAs: (name: string) => `Also listed as ${name}. Check the directions before adding both.`
  },
  quickEdit: {
    tooltip: 'Edit SIG, dose and days supply',
    ariaLabel: (name: string) => `Edit SIG, dose and days supply for ${name}`,
    sig: 'SIG',
    dose: 'Dose',
    doseWithUnit: (unit: string) => `Dose (${unit})`,
    daysSupply: 'Days supply',
    sigRequired: 'SIG is required',
    daysSupplyInvalid: 'Whole number of days, or leave empty if unknown',
    save: 'Save',
    saving: 'Saving...',
    cancel: 'Cancel'
  },
  strengthChanged: {
    ariaLabel: 'Strength changed',
    tooltip: (previous: string, previousFilled: string, filled: string) =>
      `Strength changed. ${previous} was filled ${previousFilled}, before this one (filled ${filled}). Both stay listed: check which strength the patient takes now.`
  },
  sigDiffers: {
    title: 'SIG differs from pharmacist edit',
    previous: (sig: string) => `Edited SIG on the previous fill: ${sig}`,
    dates: (edited: string, filled: string) => `Edited ${edited} · fill of ${filled}`
  },
  inactive: {
    activeTab: (count: number) => `Active · ${count}`,
    inactiveTab: (count: number) => `Inactive · ${count}`,
    caption: (shortFillDays: number, shortGraceDays: number, longGraceMonths: number) =>
      `Short fills (${shortFillDays} days or less) that ran out more than ${shortGraceDays} days ago, and longer fills with no refill on record for over ${longGraceMonths} months. The patient may have finished or stopped these, or may fill them somewhere else. Add any the patient still takes.`,
    empty: 'No inactive medications.',
    shortFillEnded: (date: string) => `Short fill · ran out ${date}`,
    noRefillSince: (date: string) => `No refill on record since ${date}`
  },
  overdueRefill: (date: string, days: number) =>
    `Refill overdue: supply ran out ${date} (${days} days ago). Ask if the patient still takes it or fills it somewhere else.`,
  toolbar: {
    sortLabel: 'Sort by',
    sort: {
      name: 'Name (A–Z)',
      fillNewest: 'Fill date (newest)',
      fillOldest: 'Fill date (oldest)',
      prescriber: 'Prescriber'
    },
    sourceLabel: 'Source',
    source: {
      all: 'All sources',
      surescripts: 'Surescripts',
      manual: 'Entered manually',
      other: 'Other imports'
    },
    prescriberLabel: 'Prescriber',
    allPrescribers: 'All prescribers'
  },
  certify: {
    kindLabel: 'Med Rec type',
    kinds: {
      INITIAL_VISIT: 'Initial Visit',
      MONTHLY: 'Monthly Med Rec',
      THREE_MONTH: '3 Month Med Rec',
      FOLLOW_UP_VISIT: 'Follow-up Visit Med Rec',
      OTHER: 'Other'
    },
    otherNameLabel: 'Name this med rec',
    otherNameRequired: 'Enter a name for this med rec.',
    kindHelper: 'Your name is saved with the med rec automatically.'
  },
  pullCount: {
    header: 'Fill Records (Medications)',
    /** `medicationCount` is null for pulls made before the backend counted distinct medications. */
    label: (fillCount: number, medicationCount: number | null) =>
      medicationCount === null
        ? `${fillCount} ${fillRecords(fillCount)}`
        : `${fillCount} ${fillRecords(fillCount)} (${medicationCount} ${medications(medicationCount)})`,
    tooltip: 'Surescripts returns one record per pharmacy fill. Med Rec shows the most recent fill of each medication.'
  },
  footer: {
    nothingToSave: 'Nothing to save',
    sendingBackForReview: (count: number) => `Locking will send ${count} ${medications(count)} back for review`,
    addedNotSaved: (count: number) => `${count} ${medications(count)} added, not saved yet`,
    keepReconciled: 'Keep them reconciled',
    undoAll: 'Undo all',
    lockAndCertify: (count: number) => `Lock & Certify ${count}`
  }
} as const;
